import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, XCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const PayoutsTab = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPayouts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/payouts/requests/pending`);
      setPayouts(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPayouts(); }, [fetchPayouts]);

  const handleReview = async (requestId, action) => {
    try {
      const res = await axios.post(`${API}/api/payouts/requests/${requestId}/review`, { action });
      toast.success(res.data.message);
      fetchPayouts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading payouts...</div>;

  return (
    <div data-testid="payouts-tab">
      <h2 className="text-lg font-semibold text-white mb-4">Pending Creator Payouts</h2>
      {payouts.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/50 border border-white/5 rounded-2xl">
          <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">No pending payout requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {payouts.map(p => {
            const m = p.payout_method || {};
            const payTo = m.method === 'zelle' ? `Zelle: ${m.zelle_email}` :
                          m.method === 'bank_transfer' ? `Bank: ${m.bank_name} / CLABE: ${m.bank_clabe}` :
                          m.method === 'crypto' ? `${m.crypto_currency}: ${m.crypto_wallet}` : 'N/A';
            return (
              <div key={p.request_id} className="bg-slate-900/80 border border-white/10 rounded-xl p-4" data-testid={`payout-${p.request_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-white font-semibold">{p.display_name || p.user_email}</p>
                    <p className="text-slate-500 text-sm">{p.user_email}</p>
                  </div>
                  <p className="text-2xl font-bold text-green-400">${p.amount.toFixed(2)}</p>
                </div>
                <div className="bg-black/30 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-500 mb-1">Pay To</p>
                  <p className="text-white font-mono text-sm">{payTo}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleReview(p.request_id, 'approve')}
                    className="flex-1 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-1"
                    data-testid={`approve-payout-${p.request_id}`}>
                    <CheckCircle className="w-4 h-4" /> Approve & Pay
                  </button>
                  <button onClick={() => handleReview(p.request_id, 'reject')}
                    className="flex-1 py-2 bg-red-500/20 text-red-400 rounded-lg font-semibold hover:bg-red-500/30 transition-colors flex items-center justify-center gap-1"
                    data-testid={`reject-payout-${p.request_id}`}>
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PayoutsTab;
